import { useEffect, useState, useCallback } from "react"
import { getProducts, createProduct, seedDefaultProducts } from "@/services/product.service"
import type { ProductWithStats } from "@/services/product.service"
import { useToast } from "@/context/ToastContext"
import { Modal, FormGroup, Input, Textarea } from "@/components/common/Modal"
import { Button } from "@/components/common/Button"
import { Badge } from "@/components/common/Badge"
import { PageLoader, EmptyState } from "@/components/common/Spinner"
import { Plus, Package, RefreshCw } from "lucide-react"

// Re-export EmptyState from correct location
import { EmptyState as Empty } from "@/components/common/EmptyState"

function ProductCard({ product, onEdit }: { product: ProductWithStats; onEdit: (p: ProductWithStats) => void }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-zimrah-200 transition-all">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{product.icon || "📱"}</span>
            <div>
              <h3 className="font-black text-gray-900 text-base leading-tight">{product.name}</h3>
              <p className="text-xs text-gray-400 mt-0.5 font-mono">{product.slug}</p>
            </div>
          </div>
          <button
            onClick={() => onEdit(product)}
            className="text-xs font-semibold text-zimrah-600 hover:text-zimrah-800 transition-colors px-2 py-1 rounded-lg hover:bg-zimrah-50"
          >
            Edit
          </button>
        </div>

        {product.description && (
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">{product.description}</p>
        )}

        {/* Price */}
        <div className="mb-4">
          <p className="text-3xl font-black text-gray-900 tracking-tight">
            ${(product.price ?? 0).toFixed(2)}
            <span className="text-sm font-medium text-gray-400 ml-1">/mo</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 bg-green-50 rounded-xl">
            <p className="text-xl font-black text-green-700">{product.activeSubs}</p>
            <p className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Active</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded-xl">
            <p className="text-xl font-black text-amber-700">{product.graceSubs}</p>
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Grace</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-xl">
            <p className="text-xl font-black text-gray-600">{product.cancelledSubs}</p>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Cancelled</p>
          </div>
        </div>

        {/* Revenue */}
        <div className="pt-4 border-t border-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Monthly Revenue</p>
            <p className="text-lg font-black text-gray-900">${product.monthlyRevenue.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ProductFormData {
  name: string
  slug: string
  description: string
  price: string
  icon: string
  url: string
}

const EMPTY_FORM: ProductFormData = {
  name: "", slug: "", description: "", price: "6.99", icon: "📱", url: "",
}

export function ProductsPage() {
  const { toast } = useToast()
  const [products, setProducts] = useState<ProductWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ProductFormData>(EMPTY_FORM)
  const [seeding, setSeeding] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await getProducts()
      setProducts(data)
    } catch {
      toast("Failed to load products", "error")
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  const handleSeed = async () => {
    setSeeding(true)
    try {
      await seedDefaultProducts()
      await load()
      toast("Default products seeded — HPC Calendar and Farmers Almanac added", "success")
    } catch {
      toast("Failed to seed products", "error")
    } finally {
      setSeeding(false)
    }
  }

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.price) {
      toast("Name, slug and price are required", "error")
      return
    }
    setSaving(true)
    try {
      await createProduct({
        name: form.name,
        slug: form.slug,
        description: form.description || null,
        price: parseFloat(form.price),
        icon: form.icon,
        url: form.url || null,
        is_active: true,
      })
      toast(`${form.name} added successfully`, "success")
      setAddOpen(false)
      setForm(EMPTY_FORM)
      await load()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error"
      toast(`Failed to save product: ${msg}`, "error")
    } finally {
      setSaving(false)
    }
  }

  const slugify = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div />
        <div className="flex gap-2">
          {products.length === 0 && (
            <Button
              variant="secondary"
              icon={<Package size={15} />}
              loading={seeding}
              onClick={handleSeed}
            >
              Seed Default Products
            </Button>
          )}
          <Button
            variant="secondary"
            icon={<RefreshCw size={15} />}
            onClick={load}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Plus size={15} />}
            onClick={() => setAddOpen(true)}
          >
            Add Product
          </Button>
        </div>
      </div>

      {/* Products grid */}
      {products.length === 0 ? (
        <Empty
          icon="📦"
          title="No products yet"
          subtitle="Add your first product or seed the defaults to get started."
          action={
            <Button variant="primary" icon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
              Add Product
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {products.map(p => (
            <ProductCard key={p.id} product={p} onEdit={() => toast("Edit coming in Phase 3", "info")} />
          ))}
        </div>
      )}

      {/* Stats table */}
      {products.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">📊 Subscriptions by Product</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {["Product", "Active", "Grace", "Cancelled", "Revenue/Mo", "Commission Rate"].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span>{p.icon}</span>
                        <span className="font-semibold text-sm text-gray-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4"><Badge variant="success">{p.activeSubs}</Badge></td>
                    <td className="px-6 py-4"><Badge variant="warning">{p.graceSubs}</Badge></td>
                    <td className="px-6 py-4"><Badge variant="muted">{p.cancelledSubs}</Badge></td>
                    <td className="px-6 py-4 font-bold text-gray-900">${p.monthlyRevenue.toFixed(2)}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      <Modal
        open={addOpen}
        onClose={() => { setAddOpen(false); setForm(EMPTY_FORM) }}
        title="Add Product"
        icon="➕"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Add Product</Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Product Name" required>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
              placeholder="HPC Calendar"
            />
          </FormGroup>
          <FormGroup label="Slug" required hint="URL-safe identifier, auto-generated">
            <Input
              value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) }))}
              placeholder="hpc-calendar"
              className="font-mono text-xs"
            />
          </FormGroup>
        </div>
        <FormGroup label="Description">
          <Textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Brief product description..."
          />
        </FormGroup>
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Monthly Price ($)" required>
            <Input
              type="number"
              step="0.01"
              value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              placeholder="6.99"
            />
          </FormGroup>
          <FormGroup label="Icon (emoji)">
            <Input
              value={form.icon}
              onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
              placeholder="📱"
            />
          </FormGroup>
        </div>
        <FormGroup label="App URL" hint="Where users access this product">
          <Input
            type="url"
            value={form.url}
            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            placeholder="https://app.zimrah.co"
          />
        </FormGroup>
      </Modal>
    </div>
  )
}